import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  Card,
  CardActions,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme
} from '@mui/material';
import {
  Refresh as RefreshIcon,
  Print as PrintIcon,
  PictureAsPdf as PictureAsPdfIcon
} from '@mui/icons-material';
import AccessTimeIcon from '@mui/icons-material/AccessTime';       // half day
import MoneyOffCsredIcon from '@mui/icons-material/MoneyOffCsred'; // no tips
import DoNotDisturbAltIcon from '@mui/icons-material/DoNotDisturbAlt'; // no service
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import { apiUrl, getSelectedBranchId, getStoredAdmin } from '../config/api';
import { APP_NAME, BRANCH_NAME } from '../config/branding';
import { toBranchDisplayName } from '../config/branchNames';

const API_URL = apiUrl('salary_management');
const SMS_API = apiUrl('send_salary_sms');

const fmt = (n) => Number(n || 0).toFixed(2);
const approxEq = (a, b, eps = 0.01) => Math.abs(Number(a) - Number(b)) <= eps;
const escapeHtml = (value) => String(value ?? '')
  .replace(/&/g, '&amp;')
  .replace(/</g, '&lt;')
  .replace(/>/g, '&gt;')
  .replace(/"/g, '&quot;')
  .replace(/'/g, '&#39;');
const EPOS_PRINTER_IP = String(process.env.REACT_APP_EPOS_PRINTER_IP || '').trim();
const EPOS_PRINTER_PORT = Number(process.env.REACT_APP_EPOS_PRINTER_PORT || 8008);
const EPOS_DEVICE_ID = String(process.env.REACT_APP_EPOS_DEVICE_ID || 'local_printer').trim();
const EPOS_SDK_URL = String(process.env.REACT_APP_EPOS_SDK_URL || '').trim();

export default function SalaryManagement() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const today = new Date();

  const [year, setYear] = useState(today.getFullYear());
  const [month, setMonth] = useState(today.getMonth() + 1);

  const [salarySlips, setSalarySlips] = useState([]);
  const [loading, setLoading] = useState(false);

  const [settlingId, setSettlingId] = useState(null);

  const [openSlip, setOpenSlip] = useState(false);
  const [slip, setSlip] = useState(null);
  const admin = useMemo(() => getStoredAdmin(), []);
  const selectedBranchId = Number(getSelectedBranchId() || admin?.active_branch_id || 0);
  const currentBranch = useMemo(() => {
    const list = Array.isArray(admin?.allowed_branches) ? admin.allowed_branches : [];
    return list.find((b) => Number(b?.id || 0) === selectedBranchId) || list[0] || null;
  }, [admin?.allowed_branches, selectedBranchId]);
  const currentBranchName = toBranchDisplayName({
    branchId: currentBranch?.id || selectedBranchId,
    branchName: currentBranch?.name || BRANCH_NAME,
    branchCode: currentBranch?.code || process.env.REACT_APP_BRANCH_CODE || '',
  });

  const resolveSlipBranchName = (salarySlip) => toBranchDisplayName({
    branchId: salarySlip?.branch_id || currentBranch?.id || selectedBranchId,
    branchName: salarySlip?.branch_name || currentBranch?.name || currentBranchName,
    branchCode: salarySlip?.branch_code || currentBranch?.code || '',
  });

  // print CSS (80mm)
  useEffect(() => {
    const style = document.createElement('style');
    style.innerHTML = `
      @media print {
        @page { size: 80mm auto; margin: 0; }
        body * { visibility: hidden; }
        .print-container, .print-container * { visibility: visible; }
        .print-container { position: absolute; top: 0; left: 0; width: 80mm; }
      }
    `;
    document.head.appendChild(style);
    return () => document.head.removeChild(style);
  }, []);

  useEffect(() => {
    if (!EPOS_SDK_URL) return;
    if (window?.epson?.ePOSDevice) return;

    const existing = document.querySelector(`script[data-epos-sdk="1"][src="${EPOS_SDK_URL}"]`);
    if (existing) return;

    const script = document.createElement('script');
    script.src = EPOS_SDK_URL;
    script.async = true;
    script.dataset.eposSdk = '1';
    document.body.appendChild(script);
  }, []);

  const fetchData = () => {
    setLoading(true);
    fetch(`${API_URL}?year=${year}&month=${month}`)
      .then((r) => r.json())
      .then((data) => {
        setSalarySlips(Array.isArray(data) ? data : []);
      })
      .catch(console.error)
      .finally(() => setLoading(false));
  };

  useEffect(fetchData, [year, month]);

  const settleSalary = (s) => {
    if (s.settled) return;
    if (!window.confirm(`Mark ${s.staff_name} as settled?`)) return;
    setSettlingId(s.staff_id);

    fetch(API_URL, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ action: 'settle', year, month, staff_id: s.staff_id })
    })
      .then((r) => r.json())
      .then((d) => {
        setSettlingId(null);
        if (d.status === 'success') fetchData();
        else alert('Error: ' + d.message);
      })
      .catch((err) => {
        console.error(err);
        setSettlingId(null);
        alert('Network error');
      });
  };

  const sendSalarySMS = (slip) => {
    const branchName = resolveSlipBranchName(slip);
    fetch(SMS_API, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        to: slip.staff_phone,
        staff_id: slip.staff_id,
        staff_name: slip.staff_name,
        branch_id: slip.branch_id || currentBranch?.id || selectedBranchId,
        branch_name: branchName,
        branch_code: slip.branch_code || currentBranch?.code || '',
        total_salary: slip.total_salary,
        year,
        month
      })
    })
      .then((r) => r.json())
      .then((d) => {
        if (d.status === 'sent') alert(`SMS sent to ${slip.staff_name}`);
        else alert('SMS error: ' + d.sms_response);
      })
      .catch(() => alert('Network error'));
  };

  const showSlip = (s) => {
    setSlip(s);
    setOpenSlip(true);
  };
  const closeSlip = () => {
    setSlip(null);
    setOpenSlip(false);
  };

  // Derived helpers for a given slip
  const summarizeSlip = (s) => {
    const perDay = Number(s.basic_salary_per_day || 0);
    const rows = Array.isArray(s.daily_breakdown) ? s.daily_breakdown : [];

    let halfDays = 0;
    let noTipDays = 0;
    let noSvcDays = 0;
    let tipTotal = 0;
    let svcTotal = 0;

    rows.forEach((d) => {
      const flagHalf =
        typeof d.flag_half !== 'undefined'
          ? !!d.flag_half
          : perDay > 0 && approxEq(d.daily_basic, perDay / 2);

      const flagNoTips =
        typeof d.flag_no_tips !== 'undefined'
          ? !!d.flag_no_tips
          : Number(d.daily_tip) === 0;

      const flagNoSvc =
        typeof d.flag_no_svc !== 'undefined'
          ? !!d.flag_no_svc
          : Number(d.daily_svc) === 0;

      if (flagHalf) halfDays++;
      if (flagNoTips) noTipDays++;
      if (flagNoSvc) noSvcDays++;

      tipTotal += Number(d.daily_tip) || 0;
      svcTotal += Number(d.daily_svc) || 0;
    });

    return {
      halfDays,
      noTipDays,
      noSvcDays,
      tipTotal,
      svcTotal
    };
  };

  const buildSlipSnapshot = (salarySlip) => {
    const sum = summarizeSlip(salarySlip);
    const rows = Array.isArray(salarySlip.daily_breakdown) ? salarySlip.daily_breakdown : [];
    const advances = Array.isArray(salarySlip.advance_payments) ? salarySlip.advance_payments : [];
    const period = `${String(month).padStart(2, '0')}/${year}`;
    const generatedAt = new Date().toLocaleString();

    const basic = Number(salarySlip.basic_earning || 0);
    const tips = Number(sum.tipTotal || 0);
    const service = Number(sum.svcTotal || 0);
    const bonus = Number(salarySlip.bonus_total || 0);
    const penalties = Number(salarySlip.penalty_total || 0);
    const loan = Number(salarySlip.loan_deduction || 0);
    const advance = Number(salarySlip.advance_deduction || 0);
    const gross = basic + tips + service + bonus;
    const deductions = penalties + loan + advance;
    const net = Number(salarySlip.total_salary || 0);

    return {
      salarySlip,
      sum,
      rows,
      advances,
      period,
      generatedAt,
      basic,
      tips,
      service,
      bonus,
      penalties,
      loan,
      advance,
      gross,
      deductions,
      net
    };
  };

  const buildReceiptTextLines = (salarySlip) => {
    if (!salarySlip) return [];
    const snapshot = buildSlipSnapshot(salarySlip);
    const branchName = resolveSlipBranchName(salarySlip);
    const {
      sum,
      rows,
      advances,
      period,
      generatedAt,
      basic,
      tips,
      service,
      bonus,
      penalties,
      loan,
      advance,
      gross,
      deductions,
      net
    } = snapshot;

    const lines = [
      `${APP_NAME} Salary Slip`,
      `${salarySlip.staff_name || ''}`,
      `Branch: ${branchName}`,
      `Period: ${period}`,
      `Generated: ${generatedAt}`,
      '----------------------------------------',
      'SUMMARY',
      `Days Present: ${salarySlip.days_present}`,
      `Basic: Rs ${fmt(basic)}`,
      `Tips: Rs ${fmt(tips)}`,
      `Service (50%): Rs ${fmt(service)}`,
      `Bonus: + Rs ${fmt(bonus)}`,
      `Penalties: - Rs ${fmt(penalties)}`,
      `Loan: - Rs ${fmt(loan)}`,
      `Advance: - Rs ${fmt(advance)}`,
      `Gross: Rs ${fmt(gross)}`,
      `Deductions: Rs ${fmt(deductions)}`,
      `NET PAY: Rs ${fmt(net)}`,
      '----------------------------------------',
      `Half Days: ${sum.halfDays}`,
      `No Tips Days: ${sum.noTipDays}`,
      `No Service Days: ${sum.noSvcDays}`,
      '----------------------------------------',
      'DAILY BREAKDOWN',
    ];

    if (!rows.length) {
      lines.push('No daily rows');
    } else {
      rows.forEach((d) => {
        const perDay = Number(salarySlip.basic_salary_per_day || 0);
        const flagHalf =
          typeof d.flag_half !== 'undefined'
            ? !!d.flag_half
            : perDay > 0 && approxEq(d.daily_basic, perDay / 2);
        const flagNoTips =
          typeof d.flag_no_tips !== 'undefined'
            ? !!d.flag_no_tips
            : Number(d.daily_tip) === 0;
        const flagNoSvc =
          typeof d.flag_no_svc !== 'undefined'
            ? !!d.flag_no_svc
            : Number(d.daily_svc) === 0;

        const flags = [
          flagHalf ? 'HALF' : null,
          flagNoTips ? 'NO-TIPS' : null,
          flagNoSvc ? 'NO-SVC' : null
        ].filter(Boolean).join(', ');

        lines.push(`${d.date}`);
        lines.push(`B:${fmt(d.daily_basic)} T:${fmt(d.daily_tip)} S:${fmt(d.daily_svc)} = ${fmt(d.daily_total)}`);
        lines.push(flags || '-');
      });
    }

    lines.push('----------------------------------------');
    lines.push('ADVANCE HISTORY');
    if (!advances.length) {
      lines.push('No advance deductions');
    } else {
      advances.forEach((a) => lines.push(`${a.date}: Rs ${fmt(a.amount)}`));
    }
    lines.push('----------------------------------------');
    lines.push('Thank you');
    return lines;
  };

  const wrapReceiptLine = (text, maxChars = 42) => {
    const raw = String(text ?? '');
    if (raw.length <= maxChars) return [raw];
    const words = raw.split(' ');
    const out = [];
    let current = '';
    words.forEach((word) => {
      if (!current) {
        current = word;
        return;
      }
      const next = `${current} ${word}`;
      if (next.length <= maxChars) {
        current = next;
      } else {
        out.push(current);
        current = word;
      }
    });
    if (current) out.push(current);
    return out.length ? out : [raw];
  };

  const downloadSlipPdf = async (salarySlip) => {
    if (!salarySlip) return;
    try {
      const { jsPDF } = await import('jspdf');
      const lines = buildReceiptTextLines(salarySlip);
      const wrapped = lines.flatMap((line) => wrapReceiptLine(line, 42));
      const lineHeight = 3.6;
      const marginX = 3;
      const marginY = 5;
      const pageHeight = Math.max(140, (marginY * 2) + (wrapped.length * lineHeight) + 5);

      const doc = new jsPDF({
        orientation: 'p',
        unit: 'mm',
        format: [80, pageHeight]
      });
      doc.setFont('courier', 'normal');
      doc.setFontSize(8.5);

      let y = marginY;
      wrapped.forEach((line) => {
        doc.text(String(line), marginX, y);
        y += lineHeight;
      });

      const safeName = String(salarySlip.staff_name || 'staff')
        .trim()
        .replace(/[^a-z0-9_-]+/gi, '_');
      const fileName = `salary_slip_${safeName}_${year}_${String(month).padStart(2, '0')}.pdf`;
      doc.save(fileName);
    } catch (err) {
      console.error(err);
      window.alert('Failed to generate salary PDF.');
    }
  };

  const printSlipEpos = async (salarySlip) => {
    if (!salarySlip) return;

    const epson = window?.epson;
    if (!epson?.ePOSDevice || !EPOS_PRINTER_IP) {
      if (!EPOS_PRINTER_IP) {
        window.alert('EPOS printer IP is not configured. Using browser 80mm print.');
      } else if (!epson?.ePOSDevice) {
        window.alert('EPOS SDK is not loaded. Using browser 80mm print.');
      }
      printSlip80mm(salarySlip);
      return;
    }

    try {
      const receiptText = buildReceiptTextLines(salarySlip).join('\n');
      await new Promise((resolve, reject) => {
        const ePosDev = new epson.ePOSDevice();
        ePosDev.connect(EPOS_PRINTER_IP, EPOS_PRINTER_PORT, (connectResult) => {
          if (connectResult !== 'OK' && connectResult !== 'SSL_CONNECT_OK') {
            reject(new Error(`EPOS connect failed: ${connectResult}`));
            return;
          }

          ePosDev.createDevice(
            EPOS_DEVICE_ID,
            ePosDev.DEVICE_TYPE_PRINTER,
            { crypto: false, buffer: false },
            (printer, retcode) => {
              if (retcode !== 'OK' || !printer) {
                reject(new Error(`EPOS createDevice failed: ${retcode}`));
                return;
              }

              try {
                printer.timeout = 60000;
                printer.onreceive = () => {
                  try { ePosDev.disconnect(); } catch { /* noop */ }
                  resolve();
                };
                printer.onerror = () => {
                  try { ePosDev.disconnect(); } catch { /* noop */ }
                  reject(new Error('EPOS printer error'));
                };

                if (typeof printer.addTextAlign === 'function' && typeof printer.ALIGN_LEFT !== 'undefined') {
                  printer.addTextAlign(printer.ALIGN_LEFT);
                }
                printer.addText(receiptText + '\n');
                if (typeof printer.addFeedLine === 'function') printer.addFeedLine(2);
                if (typeof printer.addCut === 'function') {
                  if (typeof printer.CUT_FEED !== 'undefined') printer.addCut(printer.CUT_FEED);
                  else printer.addCut();
                }
                printer.send();
              } catch (err) {
                try { ePosDev.disconnect(); } catch { /* noop */ }
                reject(err);
              }
            }
          );
        });
      });
    } catch (err) {
      console.error(err);
      window.alert('EPOS print failed. Using browser 80mm print instead.');
      printSlip80mm(salarySlip);
    }
  };

  const printSlip80mm = (salarySlip) => {
    if (!salarySlip) return;

    const snapshot = buildSlipSnapshot(salarySlip);
    const branchName = resolveSlipBranchName(salarySlip);
    const {
      sum,
      rows,
      advances,
      period,
      generatedAt,
      basic,
      tips,
      service,
      bonus,
      penalties,
      loan,
      advance,
      gross,
      deductions,
      net
    } = snapshot;

    const dailyLines = rows.map((d) => {
      const perDay = Number(salarySlip.basic_salary_per_day || 0);
      const flagHalf =
        typeof d.flag_half !== 'undefined'
          ? !!d.flag_half
          : perDay > 0 && approxEq(d.daily_basic, perDay / 2);
      const flagNoTips =
        typeof d.flag_no_tips !== 'undefined'
          ? !!d.flag_no_tips
          : Number(d.daily_tip) === 0;
      const flagNoSvc =
        typeof d.flag_no_svc !== 'undefined'
          ? !!d.flag_no_svc
          : Number(d.daily_svc) === 0;

      const flags = [
        flagHalf ? 'HALF' : null,
        flagNoTips ? 'NO-TIPS' : null,
        flagNoSvc ? 'NO-SVC' : null
      ].filter(Boolean).join(', ');

      return (
        `<div class="line">`
        + `<div><b>${escapeHtml(d.date)}</b></div>`
        + `<div>B:${fmt(d.daily_basic)} T:${fmt(d.daily_tip)} S:${fmt(d.daily_svc)} = ${fmt(d.daily_total)}</div>`
        + `<div class="muted">${flags || '-'}</div>`
        + `</div>`
      );
    }).join('');

    const advanceLines = advances.map((a) => (
      `<div class="line"><div>${escapeHtml(a.date)}: Rs ${fmt(a.amount)}</div></div>`
    )).join('');

    const html = `<!doctype html>
<html>
<head>
  <meta charset="utf-8" />
  <title>Salary Slip - ${escapeHtml(salarySlip.staff_name || '')} - ${escapeHtml(branchName)}</title>
  <style>
    @page { size: 80mm auto; margin: 2mm; }
    * { box-sizing: border-box; }
    body {
      margin: 0;
      width: 80mm;
      font-family: "Courier New", monospace;
      font-size: 11px;
      line-height: 1.35;
      color: #111;
    }
    .wrap { width: 76mm; margin: 0 auto; padding: 2mm 1mm; }
    .center { text-align: center; }
    .title { font-size: 13px; font-weight: 700; }
    .staff { font-size: 12px; font-weight: 700; margin-top: 2px; }
    .muted { color: #555; font-size: 10px; }
    .divider { border-top: 1px dashed #000; margin: 5px 0; }
    .row { display: flex; justify-content: space-between; gap: 8px; }
    .row .label { flex: 1; }
    .row .value { min-width: 26mm; text-align: right; }
    .line { margin: 4px 0; padding-bottom: 3px; border-bottom: 1px dotted #ddd; }
    .section-title { font-weight: 700; text-transform: uppercase; margin: 4px 0; font-size: 10px; }
    .net { font-size: 13px; font-weight: 700; }
  </style>
</head>
<body>
  <div class="wrap">
    <div class="center title">${escapeHtml(APP_NAME)} Salary Slip</div>
    <div class="center staff">${escapeHtml(salarySlip.staff_name || '')}</div>
    <div class="center muted">Branch: ${escapeHtml(branchName)}</div>
    <div class="center muted">Period: ${escapeHtml(period)}</div>
    <div class="center muted">Generated: ${escapeHtml(generatedAt)}</div>

    <div class="divider"></div>
    <div class="section-title">Summary</div>
    <div class="row"><div class="label">Days Present</div><div class="value">${escapeHtml(salarySlip.days_present)}</div></div>
    <div class="row"><div class="label">Basic</div><div class="value">Rs ${fmt(basic)}</div></div>
    <div class="row"><div class="label">Tips</div><div class="value">Rs ${fmt(tips)}</div></div>
    <div class="row"><div class="label">Service (50%)</div><div class="value">Rs ${fmt(service)}</div></div>
    <div class="row"><div class="label">Bonus</div><div class="value">+ Rs ${fmt(bonus)}</div></div>
    <div class="row"><div class="label">Penalties</div><div class="value">- Rs ${fmt(penalties)}</div></div>
    <div class="row"><div class="label">Loan</div><div class="value">- Rs ${fmt(loan)}</div></div>
    <div class="row"><div class="label">Advance</div><div class="value">- Rs ${fmt(advance)}</div></div>
    <div class="row"><div class="label">Gross</div><div class="value">Rs ${fmt(gross)}</div></div>
    <div class="row"><div class="label">Deductions</div><div class="value">Rs ${fmt(deductions)}</div></div>
    <div class="divider"></div>
    <div class="row net"><div class="label">NET PAY</div><div class="value">Rs ${fmt(net)}</div></div>

    <div class="divider"></div>
    <div class="section-title">Applied Options</div>
    <div class="row"><div class="label">Half Days</div><div class="value">${escapeHtml(sum.halfDays)}</div></div>
    <div class="row"><div class="label">No Tips Days</div><div class="value">${escapeHtml(sum.noTipDays)}</div></div>
    <div class="row"><div class="label">No Service Days</div><div class="value">${escapeHtml(sum.noSvcDays)}</div></div>

    <div class="divider"></div>
    <div class="section-title">Daily Breakdown</div>
    ${dailyLines || '<div class="muted">No daily rows</div>'}

    <div class="divider"></div>
    <div class="section-title">Advance History</div>
    ${advanceLines || '<div class="muted">No advance deductions</div>'}

    <div class="divider"></div>
    <div class="center muted">Thank you</div>
  </div>
</body>
</html>`;

    const printWin = window.open('', '_blank', 'width=480,height=900');
    if (!printWin) {
      window.alert('Please allow popups to print/download salary slip.');
      return;
    }

    printWin.document.open();
    printWin.document.write(html);
    printWin.document.close();
    printWin.focus();
    setTimeout(() => {
      printWin.print();
    }, 250);
  };

  const Legend = () => (
    <Stack
      direction="row"
      spacing={1}
      sx={{
        alignItems: 'center',
        flexWrap: 'wrap',
        gap: 1,
        p: 1,
        borderRadius: 2,
        bgcolor: 'background.paper'
      }}
    >
      <Chip
        icon={<AccessTimeIcon />}
        label="Half Day applied"
        size="small"
        variant="outlined"
      />
      <Chip
        icon={<MoneyOffCsredIcon />}
        label="No Tips"
        size="small"
        variant="outlined"
        color="warning"
      />
      <Chip
        icon={<DoNotDisturbAltIcon />}
        label="No Service"
        size="small"
        variant="outlined"
        color="error"
      />
      <Tooltip title="A zero tips/service value could mean the pool was zero that day or the staff was excluded. If your backend sets flag_no_tips / flag_no_svc, we show the exact reason.">
        <InfoOutlinedIcon fontSize="small" style={{ opacity: 0.7 }} />
      </Tooltip>
    </Stack>
  );

  return (
    <Box sx={{ p: 2 }}>
      <Typography variant="h4" gutterBottom>
        {APP_NAME} Salary Management
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        {currentBranchName}
      </Typography>

      {/* Filters */}
      <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap' }}>
        <TextField
          label="Year"
          size="small"
          type="number"
          value={year}
          onChange={(e) => setYear(+e.target.value)}
        />
        <TextField
          label="Month"
          size="small"
          type="number"
          inputProps={{ min: 1, max: 12 }}
          value={month}
          onChange={(e) => setMonth(+e.target.value)}
        />
        <Button startIcon={<RefreshIcon />} variant="contained" onClick={fetchData}>
          Refresh
        </Button>
        <Legend />
      </Box>

      {/* Salary List */}
      {loading ? (
        <Box sx={{ textAlign: 'center', py: 4 }}>
          <CircularProgress />
        </Box>
      ) : salarySlips.length === 0 ? (
        <Typography>No data for {month}/{year}.</Typography>
      ) : isMobile ? (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {salarySlips.map((s) => {
            const sum = summarizeSlip(s);
            return (
              <Card key={s.staff_id} variant="outlined" sx={{ overflow: 'hidden' }}>
                <CardContent>
                  <Stack direction="row" justifyContent="space-between" alignItems="center">
                    <Typography variant="h6">{s.staff_name}</Typography>
                    <Chip
                      size="small"
                      label={s.settled ? 'Settled' : 'Pending'}
                      color={s.settled ? 'success' : 'default'}
                    />
                  </Stack>

                  <Divider sx={{ my: 1.5 }} />

                  {/* Summary bullets */}
                  <Stack spacing={0.5}>
                    <Typography><b>Days Present</b>: {s.days_present}</Typography>
                    <Typography><b>Basic</b>: Rs {fmt(s.basic_earning)}</Typography>
                    <Typography><b>Tips</b>: Rs {fmt(sum.tipTotal)} &nbsp;|&nbsp; <b>Service</b>: Rs {fmt(sum.svcTotal)}</Typography>
                    <Typography sx={{ color: 'green' }}><b>Bonus</b>: +Rs {fmt(s.bonus_total)}</Typography>
                    <Typography sx={{ color: 'red' }}><b>Penalties</b>: −Rs {fmt(s.penalty_total)}</Typography>
                    <Typography><b>Loan</b>: −Rs {fmt(s.loan_deduction)}</Typography>
                    <Typography><b>Advance</b>: −Rs {fmt(s.advance_deduction)}</Typography>
                    <Typography variant="h6" sx={{ mt: 1 }}>
                      Net: Rs {fmt(s.total_salary)}
                    </Typography>
                  </Stack>

                  {/* Flags summary */}
                  <Divider sx={{ my: 1.5 }} />
                  <Typography variant="subtitle2" gutterBottom>Applied Options (Counts)</Typography>
                  <Stack direction="row" spacing={1} flexWrap="wrap">
                    <Chip icon={<AccessTimeIcon />} label={`Half Days: ${sum.halfDays}`} size="small" />
                    <Chip icon={<MoneyOffCsredIcon />} label={`No Tips Days: ${sum.noTipDays}`} size="small" color="warning" />
                    <Chip icon={<DoNotDisturbAltIcon />} label={`No Service Days: ${sum.noSvcDays}`} size="small" color="error" />
                  </Stack>
                </CardContent>
                <CardActions sx={{ justifyContent: 'space-between', px: 2, pb: 2 }}>
                  <Button size="small" startIcon={<ReceiptLongIcon />} variant="outlined" onClick={() => showSlip(s)}>
                    View Slip
                  </Button>
                  <Stack direction="row" spacing={1}>
                    <Button size="small" variant="outlined" onClick={() => sendSalarySMS(s)}>
                      SMS Salary Sheet
                    </Button>
                    <Button
                      size="small"
                      variant="contained"
                      disabled={s.settled || settlingId === s.staff_id}
                      onClick={() => settleSalary(s)}
                    >
                      {settlingId === s.staff_id
                        ? 'Settling…'
                        : s.settled
                          ? 'Settled'
                          : 'Settle'}
                    </Button>
                  </Stack>
                </CardActions>
              </Card>
            );
          })}
        </Box>
      ) : (
        <Paper>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Staff</TableCell>
                <TableCell align="right">Days</TableCell>
                <TableCell align="right">Basic</TableCell>
                <TableCell align="right">Tips</TableCell>
                <TableCell align="right">Service</TableCell>
                <TableCell align="right">Bonus</TableCell>
                <TableCell align="right">Penalties</TableCell>
                <TableCell align="right">Loan</TableCell>
                <TableCell align="right">Advance</TableCell>
                <TableCell align="right">Net</TableCell>
                <TableCell align="center">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {salarySlips.map((s) => {
                const sum = summarizeSlip(s);
                return (
                  <TableRow key={s.staff_id}>
                    <TableCell>
                      <Stack spacing={0.5}>
                        <Typography sx={{ fontWeight: 600 }}>{s.staff_name}</Typography>
                        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                          <Chip size="small" icon={<AccessTimeIcon />} label={`Half: ${sum.halfDays}`} />
                          <Chip size="small" icon={<MoneyOffCsredIcon />} label={`No Tips: ${sum.noTipDays}`} color="warning" />
                          <Chip size="small" icon={<DoNotDisturbAltIcon />} label={`No Svc: ${sum.noSvcDays}`} color="error" />
                        </Stack>
                      </Stack>
                    </TableCell>
                    <TableCell align="right">{s.days_present}</TableCell>
                    <TableCell align="right">{fmt(s.basic_earning)}</TableCell>
                    <TableCell align="right">{fmt(sum.tipTotal)}</TableCell>
                    <TableCell align="right">{fmt(sum.svcTotal)}</TableCell>
                    <TableCell align="right" sx={{ color: 'green' }}>{fmt(s.bonus_total)}</TableCell>
                    <TableCell align="right" sx={{ color: 'red' }}>{fmt(s.penalty_total)}</TableCell>
                    <TableCell align="right">{fmt(s.loan_deduction)}</TableCell>
                    <TableCell align="right">{fmt(s.advance_deduction)}</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 700 }}>{fmt(s.total_salary)}</TableCell>
                    <TableCell align="center">
                      <Stack direction="row" spacing={1} justifyContent="center">
                        <Button size="small" variant="outlined" onClick={() => showSlip(s)}>View Slip</Button>
                        <Button size="small" variant="outlined" onClick={() => sendSalarySMS(s)}>SMS Salary Sheet</Button>
                        <Button
                          size="small"
                          variant="contained"
                          disabled={s.settled || settlingId === s.staff_id}
                          onClick={() => settleSalary(s)}
                        >
                          {settlingId === s.staff_id
                            ? 'Settling…'
                            : s.settled
                              ? 'Settled'
                              : 'Settle'}
                        </Button>
                      </Stack>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </Paper>
      )}

      {/* Slip Dialog */}
      <Dialog open={openSlip} onClose={closeSlip} maxWidth="md" fullWidth>
        <DialogTitle>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 2 }}>
            {APP_NAME} Salary Slip
            <IconButton onClick={() => printSlip80mm(slip)} size="small">
              <PrintIcon fontSize="inherit" />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent dividers>
          {slip && (
            <Box className="print-container" sx={{ p: 1 }}>
              {/* Header */}
              <Typography variant="h6" align="center">{slip.staff_name}</Typography>
              <Typography align="center" color="text.secondary">
                Branch: {resolveSlipBranchName(slip)}
              </Typography>
              <Typography align="center" color="text.secondary" gutterBottom>
                Period: {String(month).padStart(2, '0')}/{year}
              </Typography>

              {/* Summary Cards */}
              <Paper variant="outlined" sx={{ my: 2, p: 2 }}>
                {(() => {
                  const sum = summarizeSlip(slip);
                  const tipTotal = sum.tipTotal;
                  const svcTotal = sum.svcTotal;
                  return (
                    <Table size="small">
                      <TableBody>
                        <TableRow>
                          <TableCell>Days Present</TableCell>
                          <TableCell align="right">{slip.days_present}</TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell>Basic Earned</TableCell>
                          <TableCell align="right">Rs {fmt(slip.basic_earning)}</TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell>Tips Share</TableCell>
                          <TableCell align="right">Rs {fmt(tipTotal)}</TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell>Service‐Charge Share</TableCell>
                          <TableCell align="right">Rs {fmt(svcTotal)}</TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell sx={{ color: 'green' }}>Bonus</TableCell>
                          <TableCell align="right" sx={{ color: 'green' }}>+ Rs {fmt(slip.bonus_total)}</TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell sx={{ color: 'red' }}>Penalties</TableCell>
                          <TableCell align="right" sx={{ color: 'red' }}>− Rs {fmt(slip.penalty_total)}</TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell>Loan Deduction</TableCell>
                          <TableCell align="right">− Rs {fmt(slip.loan_deduction)}</TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell>Advance Deduction</TableCell>
                          <TableCell align="right">− Rs {fmt(slip.advance_deduction)}</TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell><b>Net Pay</b></TableCell>
                          <TableCell align="right"><b>Rs {fmt(slip.total_salary)}</b></TableCell>
                        </TableRow>
                      </TableBody>
                    </Table>
                  );
                })()}
              </Paper>

              {/* Option counts */}
              <Stack direction="row" spacing={1} sx={{ mb: 1 }} flexWrap="wrap">
                {(() => {
                  const sum = summarizeSlip(slip);
                  return (
                    <>
                      <Chip icon={<AccessTimeIcon />} label={`Half Days: ${sum.halfDays}`} size="small" />
                      <Chip icon={<MoneyOffCsredIcon />} label={`No Tips: ${sum.noTipDays}`} size="small" color="warning" />
                      <Chip icon={<DoNotDisturbAltIcon />} label={`No Service: ${sum.noSvcDays}`} size="small" color="error" />
                    </>
                  );
                })()}
              </Stack>

              {/* Daily Breakdown */}
              <Typography variant="subtitle1" gutterBottom>Daily Breakdown</Typography>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Date</TableCell>
                    <TableCell align="right">Basic</TableCell>
                    <TableCell align="right">Tips</TableCell>
                    <TableCell align="right">Svc (50%)</TableCell>
                    <TableCell align="right">Total</TableCell>
                    <TableCell>Flags</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {(Array.isArray(slip.daily_breakdown) ? slip.daily_breakdown : []).map((d, i) => {
                    const perDay = Number(slip.basic_salary_per_day || 0);
                    const flagHalf =
                      typeof d.flag_half !== 'undefined'
                        ? !!d.flag_half
                        : perDay > 0 && approxEq(d.daily_basic, perDay / 2);
                    const flagNoTips =
                      typeof d.flag_no_tips !== 'undefined'
                        ? !!d.flag_no_tips
                        : Number(d.daily_tip) === 0;
                    const flagNoSvc =
                      typeof d.flag_no_svc !== 'undefined'
                        ? !!d.flag_no_svc
                        : Number(d.daily_svc) === 0;

                    return (
                      <TableRow key={i}>
                        <TableCell>{d.date}</TableCell>
                        <TableCell align="right">{fmt(d.daily_basic)}</TableCell>
                        <TableCell align="right">{fmt(d.daily_tip)}</TableCell>
                        <TableCell align="right">{fmt(d.daily_svc)}</TableCell>
                        <TableCell align="right">{fmt(d.daily_total)}</TableCell>
                        <TableCell>
                          <Stack direction="row" spacing={1} flexWrap="wrap">
                            {flagHalf && (
                              <Chip
                                size="small"
                                icon={<AccessTimeIcon />}
                                label="Half Day"
                                variant="outlined"
                              />
                            )}
                            {flagNoTips && (
                              <Tooltip title={d.flag_no_tips === false ? 'No tips (pool was zero)' : 'Tips excluded'}>
                                <Chip
                                  size="small"
                                  color="warning"
                                  icon={<MoneyOffCsredIcon />}
                                  label="No Tips"
                                  variant="outlined"
                                />
                              </Tooltip>
                            )}
                            {flagNoSvc && (
                              <Tooltip title={d.flag_no_svc === false ? 'No service (pool was zero)' : 'Service excluded'}>
                                <Chip
                                  size="small"
                                  color="error"
                                  icon={<DoNotDisturbAltIcon />}
                                  label="No Service"
                                  variant="outlined"
                                />
                              </Tooltip>
                            )}
                            {!flagHalf && !flagNoTips && !flagNoSvc && <Typography variant="caption" color="text.secondary">—</Typography>}
                          </Stack>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                  {!Array.isArray(slip.daily_breakdown) || slip.daily_breakdown.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={6} align="center">
                        No daily breakdown data available
                      </TableCell>
                    </TableRow>
                  ) : null}
                </TableBody>
              </Table>

              {/* Advance history inline (if present) */}
              {Array.isArray(slip.advance_payments) && slip.advance_payments.length > 0 && (
                <>
                  <Divider sx={{ my: 2 }} />
                  <Typography variant="subtitle2" gutterBottom>Advance History</Typography>
                  <Stack spacing={0.5}>
                    {slip.advance_payments.map((a, idx) => (
                      <Typography key={idx} variant="body2">
                        {a.date}: Rs {fmt(a.amount)}
                      </Typography>
                    ))}
                  </Stack>
                </>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            variant="outlined"
            startIcon={<PictureAsPdfIcon />}
            onClick={() => downloadSlipPdf(slip)}
            disabled={!slip}
          >
            Download PDF
          </Button>
          <Button
            variant="outlined"
            startIcon={<PrintIcon />}
            onClick={() => printSlipEpos(slip)}
            disabled={!slip}
          >
            EPOS Print
          </Button>
          <Button
            variant="outlined"
            startIcon={<PrintIcon />}
            onClick={() => printSlip80mm(slip)}
            disabled={!slip}
          >
            80mm Print
          </Button>
          <Button onClick={closeSlip}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
